import { createSlice } from '@reduxjs/toolkit';
import { RefereeDTO, ReferralProgramDTO, ReferralStatsDTO, ReferrerDTO } from '../types';
import {
  fetchRefereesThunk,
  fetchReferralProgramThunk,
  fetchReferralStatsThunk,
  fetchReferrersThunk,
  saveReferralProgramThunk,
  saveReferralTiersThunk,
  validateReferralThunk,
} from './referral.thunk';

export interface ReferralState {
  program: ReferralProgramDTO | null;
  isProgramLoading: boolean;
  isSaving: boolean;
  stats: ReferralStatsDTO | null;
  isStatsLoading: boolean;
  hasStatsError: boolean;
  referrers: ReferrerDTO[];
  referrersTotal: number;
  isReferrersLoading: boolean;
  hasReferrersError: boolean;
  refereesByReferrer: Record<string, RefereeDTO[]>;
  loadingRefereesFor: Record<string, boolean>;
  validatingReferralIds: string[];
}

const initialState: ReferralState = {
  program: null,
  isProgramLoading: false,
  isSaving: false,
  stats: null,
  isStatsLoading: false,
  hasStatsError: false,
  referrers: [],
  referrersTotal: 0,
  isReferrersLoading: false,
  hasReferrersError: false,
  refereesByReferrer: {},
  loadingRefereesFor: {},
  validatingReferralIds: [],
};

const stopValidating = (state: ReferralState, referralId: string) =>
  state.validatingReferralIds.filter(id => id !== referralId);

export const { actions, reducer } = createSlice({
  name: 'referral',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchReferralProgramThunk.pending, state => {
      state.isProgramLoading = true;
    });
    builder.addCase(fetchReferralProgramThunk.fulfilled, (state, action) => {
      state.program = action.payload;
      state.isProgramLoading = false;
    });
    builder.addCase(fetchReferralProgramThunk.rejected, state => {
      state.isProgramLoading = false;
    });

    builder.addCase(saveReferralProgramThunk.pending, state => {
      state.isSaving = true;
    });
    builder.addCase(saveReferralProgramThunk.fulfilled, (state, action) => {
      state.program = action.payload;
      state.isSaving = false;
    });
    builder.addCase(saveReferralProgramThunk.rejected, state => {
      state.isSaving = false;
    });

    builder.addCase(saveReferralTiersThunk.pending, state => {
      state.isSaving = true;
    });
    builder.addCase(saveReferralTiersThunk.fulfilled, (state, action) => {
      state.program = action.payload;
      state.isSaving = false;
    });
    builder.addCase(saveReferralTiersThunk.rejected, state => {
      state.isSaving = false;
    });

    builder.addCase(fetchReferralStatsThunk.pending, state => {
      state.isStatsLoading = true;
      state.hasStatsError = false;
    });
    builder.addCase(fetchReferralStatsThunk.fulfilled, (state, action) => {
      state.stats = action.payload;
      state.isStatsLoading = false;
    });
    builder.addCase(fetchReferralStatsThunk.rejected, state => {
      state.isStatsLoading = false;
      state.hasStatsError = true;
    });

    builder.addCase(fetchReferrersThunk.pending, state => {
      state.isReferrersLoading = true;
      state.hasReferrersError = false;
    });
    builder.addCase(fetchReferrersThunk.fulfilled, (state, action) => {
      state.referrers = action.payload.items;
      state.referrersTotal = action.payload.total;
      state.isReferrersLoading = false;
      state.refereesByReferrer = {};
    });
    builder.addCase(fetchReferrersThunk.rejected, state => {
      state.isReferrersLoading = false;
      state.hasReferrersError = true;
    });

    builder.addCase(fetchRefereesThunk.pending, (state, action) => {
      state.loadingRefereesFor[action.meta.arg] = true;
    });
    builder.addCase(fetchRefereesThunk.fulfilled, (state, action) => {
      state.refereesByReferrer[action.payload.referrerId] = action.payload.referees;
      delete state.loadingRefereesFor[action.payload.referrerId];
    });
    builder.addCase(fetchRefereesThunk.rejected, (state, action) => {
      delete state.loadingRefereesFor[action.meta.arg];
    });

    builder.addCase(validateReferralThunk.pending, (state, action) => {
      state.validatingReferralIds.push(action.meta.arg.referralId);
    });
    builder.addCase(validateReferralThunk.fulfilled, (state, action) => {
      state.validatingReferralIds = stopValidating(state, action.meta.arg.referralId);
    });
    builder.addCase(validateReferralThunk.rejected, (state, action) => {
      state.validatingReferralIds = stopValidating(state, action.meta.arg.referralId);
    });
  },
});
